import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import hotelImg from "../assests/images/hotel-bg.jpg";
import Hero from "../components/Hero";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaSearch,
    FaMoneyBillWave,
    FaMapMarkedAlt,
    FaUndo
} from "react-icons/fa";



export default function HotelList({
    limit,
    showViewAllButton = false,
    showSidebar = true,
    showBanner = false,
    showHeading = false   // ✅ ADD THIS
}) {

    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);

    // Wishlist
    const [wishlist, setWishlist] = useState([]);

    // Filters
    const [search, setSearch] = useState("");
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(5000);
    const [rating, setRating] = useState(0);

    const [destinations, setDestinations] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState([]);

    const navigate = useNavigate();

    // ✅ LOAD WISHLIST FROM LOCALSTORAGE
    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlist(storedWishlist);
    }, []);

    // ✅ UPDATE WISHLIST
    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    // ✅ TOGGLE WISHLIST
    const toggleWishlist = (hotel) => {
        const exists = wishlist.find(item => item.id === hotel.id);

        let updated;
        if (exists) {
            updated = wishlist.filter(item => item.id !== hotel.id);
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    // ✅ CHECK IF IN WISHLIST
    const isWishlisted = (id) => {
        return wishlist.some(item => item.id === id);
    };

    // Fetch Destinations
    const fetchDestinations = async () => {
        try {
            const res = await axios.get("https://hotel.southtravels.com/api/destinations");
            setDestinations(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    const handleDestinationChange = (dest) => {
        setSelectedDestinations((prev) =>
            prev.some(d => d.id === dest.id)
                ? prev.filter((d) => d.id !== dest.id)
                : [...prev, dest]
        );
    };

    // Debounce search
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    // Fetch Hotels
    const fetchHotels = async () => {
        setLoading(true);
        try {
            const res = await axios.get("https://hotel.southtravels.com/api/hotels", {
                params: {
                    search: debouncedSearch,
                    destinations: selectedDestinations.map(d => d.id),
                    minPrice,
                    maxPrice,
                    rating,
                },
            });

            setHotels(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHotels();
    }, [debouncedSearch, minPrice, maxPrice, rating, selectedDestinations]);

    // Stars
    const renderStars = (ratingValue) => {
        return Array.from({ length: 5 }, (_, i) => {
            if (i < Math.floor(ratingValue)) return <FaStar key={i} />;
            if (i === Math.floor(ratingValue) && ratingValue % 1 >= 0.5)
                return <FaStarHalfAlt key={i} />;
            return <FaRegStar key={i} />;
        });
    };

    // Reset Filters
    const isFilterChanged =
        search !== "" ||
        minPrice !== 0 ||
        maxPrice !== 5000 ||
        rating !== 0 ||
        selectedDestinations.length > 0;

    const resetFilters = () => {
        setSearch("");
        setMinPrice(0);
        setMaxPrice(5000);
        setRating(0);
        setSelectedDestinations([]);
    };

    return (
        <div className="w-full">
            {showBanner && (
                <Hero
                    variant="inner"
                    title="All Hotels"
                    subtitle="Explore the best stays and experiences"
                    backgroundImage={hotelImg}
                />
            )}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
                {/* Heading */}
                {showHeading && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-10">
                        <div>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                                Available Hotels
                            </h2>
                            <p className="text-sm sm:text-base text-gray-500 mt-2">
                                Find the perfect stay for your next trip
                            </p>
                        </div>

                        {/* Desktop Button */}
                        <button
                            onClick={() => navigate("/hotels")}
                            className="hidden sm:inline-flex bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition text-sm sm:text-base"
                        >
                            View All
                        </button>
                    </div>
                )}

                {/* Layout */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Sidebar */}
                    {showSidebar && (
                        <div className="w-full lg:w-1/4">
                            <div className="lg:sticky lg:top-6">
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-4 sm:p-5 lg:p-6 space-y-6 sm:space-y-8">
                                    {/* Header */}
                                    <div className="flex justify-between items-center gap-3">
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
                                            ⚙️ Filters
                                        </h3>

                                        {isFilterChanged && (
                                            <button
                                                onClick={resetFilters}
                                                className="flex items-center gap-1 text-xs bg-red-50 text-red-500 px-3 py-1.5 rounded-full hover:bg-red-100 transition"
                                            >
                                                <FaUndo />
                                                Reset
                                            </button>
                                        )}
                                    </div>

                                    {/* Search */}
                                    <div>
                                        <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                                            <FaSearch className="text-teal-500" />
                                            Search
                                        </label>

                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Hotel or destination..."
                                            className="w-full mt-3 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-teal-400 transition shadow-sm text-sm sm:text-base"
                                        />
                                    </div>

                                    {/* Price */}
                                    <div>
                                        <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                                            <FaMoneyBillWave className="text-green-500" />
                                            Price Range
                                        </label>

                                        <div className="flex flex-col sm:flex-row gap-3 mt-3">
                                            <input
                                                type="number"
                                                value={minPrice}
                                                onChange={(e) => setMinPrice(e.target.value)}
                                                placeholder="Min"
                                                className="w-full sm:w-1/2 px-3 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 shadow-sm text-sm sm:text-base"
                                            />

                                            <input
                                                type="number"
                                                value={maxPrice}
                                                onChange={(e) => setMaxPrice(e.target.value)}
                                                placeholder="Max"
                                                className="w-full sm:w-1/2 px-3 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 shadow-sm text-sm sm:text-base"
                                            />
                                        </div>
                                    </div>

                                    {/* Rating */}
                                    <div>
                                        <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                                            <FaStar className="text-yellow-500" />
                                            Rating
                                        </label>

                                        <select
                                            value={rating}
                                            onChange={(e) => setRating(e.target.value)}
                                            className="w-full mt-3 px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-teal-500 shadow-sm text-sm sm:text-base"
                                        >
                                            <option value="0">All Ratings</option>
                                            <option value="3">3★ & above</option>
                                            <option value="4">4★ & above</option>
                                            <option value="4.5">4.5★ & above</option>
                                        </select>
                                    </div>

                                    {/* Destinations */}
                                    <div>
                                        <label className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                                            <FaMapMarkedAlt className="text-blue-500" />
                                            Destinations
                                        </label>

                                        <div className="mt-3 max-h-52 overflow-y-auto space-y-2 pr-1">
                                            {destinations.map((dest) => {
                                                const isChecked = selectedDestinations.some(
                                                    (d) => d.id === dest.id
                                                );

                                                return (
                                                    <label
                                                        key={dest.id}
                                                        className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition border ${isChecked
                                                            ? "bg-teal-100 border-teal-400 shadow-sm"
                                                            : "border-transparent hover:bg-gray-100"
                                                            }`}
                                                    >
                                                        <span className="text-sm text-gray-700">
                                                            {dest.name}
                                                        </span>

                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => handleDestinationChange(dest)}
                                                            className="accent-teal-500"
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Hotels */}
                    <div className={showSidebar ? "w-full lg:w-3/4" : "w-full"}>
                        {loading ? (
                            <div className="flex justify-center py-20">
                                <div className="animate-spin h-12 w-12 border-4 border-teal-500 border-t-transparent rounded-full"></div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
                                    {(limit ? hotels.slice(0, limit) : hotels).map((hotel) => (
                                        <div
                                            key={hotel.id}
                                            className="group bg-white rounded-3xl shadow-md hover:shadow-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                                        >
                                            {/* Image */}
                                            <div
                                                className="relative h-52 sm:h-56 md:h-60 cursor-pointer overflow-hidden"
                                                onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                            >
                                                <img
                                                    src={`https://hotel.southtravels.com/uploads/hotels/${hotel.image}`}
                                                    alt={hotel.name}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />

                                                {/* Wishlist */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleWishlist(hotel);
                                                    }}
                                                    className="absolute top-3 right-3 bg-white/90 p-2 rounded-full shadow hover:scale-110 transition"
                                                >
                                                    {isWishlisted(hotel.id) ? (
                                                        <FaHeart className="text-red-500 text-sm sm:text-base" />
                                                    ) : (
                                                        <FaRegHeart className="text-gray-600 text-sm sm:text-base" />
                                                    )}
                                                </button>
                                            </div>

                                            {/* Content */}
                                            <div className="p-4 sm:p-5">
                                                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 line-clamp-1">
                                                    {hotel.name}
                                                </h3>

                                                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                                    <FaMapMarkerAlt className="shrink-0 text-blue-900" />
                                                    {hotel.location || "Dubai"}
                                                </p>

                                                <div className="flex items-center text-yellow-500 mt-3 text-sm">
                                                    {renderStars(hotel.rating)}
                                                </div>

                                                {/* Price */}
                                                <div className="mt-4 flex items-end justify-between">
                                                    <div>
                                                        <p className="text-xs text-gray-500">Starting from</p>
                                                        <h4 className="text-2xl font-bold text-teal-600">
                                                            AED {Math.round(hotel.price_per_night)}
                                                        </h4>
                                                    </div>

                                                    <span className="text-sm text-gray-500 mb-1">
                                                        / night
                                                    </span>
                                                </div>

                                                <button
                                                    onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                                    className="mt-4 w-full bg-teal-500 hover:bg-teal-600 text-white py-2.5 rounded-xl transition text-sm sm:text-base font-medium"
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Mobile View All Button */}
                                {showHeading && (
                                    <div className="mt-8 sm:hidden">
                                        <button
                                            onClick={() => navigate("/hotels")}
                                            className="w-full bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition text-sm"
                                        >
                                            View All
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}